import React, { useState } from 'react';
import { 
  Menu, 
  X, 
  Home, 
  Users, 
  Calendar, 
  Eye, 
  BarChart3, 
  MessageSquare, 
  Database, 
  Settings,
  Building2,
  LogOut
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { PermissionGuard, Permission } from './PermissionGuard';
import NotificationBell from './NotificationBell';

/**
 * Format role value for display (e.g., "super_admin" -> "Super Admin")
 */
const formatRoleDisplay = (role) => {
  if (!role) return '';
  return role.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

/**
 * Navigation items with permission requirements
 * Requirements: 7.2 - Frontend displays only menu items user has permission to access
 */
const sidebarItemsConfig = [
  { id: 'dashboard', label: 'Dashboard', icon: Home, permission: null }, // Dashboard accessible to all authenticated users
  { id: 'employees', label: 'Employees', icon: Users, permission: Permission.VIEW_EMPLOYEES },
  { id: 'performance', label: 'Performance', icon: BarChart3, permission: Permission.VIEW_REPORTS },
  { id: 'absences', label: 'Absences', icon: Calendar, permission: Permission.VIEW_ABSENCES },
  { id: 'observations', label: 'Observations', icon: Eye, permission: Permission.VIEW_OBSERVATIONS },
  { id: 'aiChat', label: 'AI Assistant', icon: MessageSquare, permission: null }, // AI Assistant accessible to all
  { id: 'dataSync', label: 'Data Sync', icon: Database, permission: Permission.MANAGE_INTEGRATIONS },
  { id: 'settings', label: 'Settings', icon: Settings, permission: Permission.MANAGE_SYSTEM }
];

const MobileNavigation = ({ 
  currentView, 
  setCurrentView, 
  currentUser, 
  userRoles, 
  handleLogout, 
  unreadCount 
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleNavClick = (itemId) => {
    setCurrentView(itemId);
    setIsOpen(false);
  };

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsOpen(true)}
            className="p-2"
            aria-label="Open navigation menu"
          >
            <Menu className="w-6 h-6" />
          </Button>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-black" />
            </div>
            <div>
              <h1 className="text-lg font-bold">PeoplePulse</h1>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <NotificationBell className="lg:hidden" />
        </div>
      </div>

      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Sidebar */}
      <aside 
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-80 bg-black text-white transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        role="navigation"
        aria-label="Mobile navigation menu"
        aria-hidden={!isOpen}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-6 border-b border-gray-800 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-black" />
              </div>
              <div>
                <h1 className="text-xl font-bold">PeoplePulse</h1>
                <p className="text-xs text-gray-400">HR Management</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="p-2 text-gray-400 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
              aria-label="Close navigation menu"
            >
              <X className="w-6 h-6" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto" role="navigation" aria-label="Primary navigation">
            {sidebarItemsConfig.map((item) => {
              const Icon = item.icon;
              const navButton = (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`w-full flex items-center px-4 py-4 rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black ${
                    currentView === item.id 
                      ? 'bg-yellow-500 text-black shadow-lg' 
                      : 'text-gray-300 hover:text-white hover:bg-gray-800'
                  }`}
                  aria-current={currentView === item.id ? 'page' : undefined}
                  aria-label={`Navigate to ${item.label}`}
                >
                  <Icon className="w-5 h-5 mr-3" aria-hidden="true" />
                  {item.label}
                </button>
              );

              // Wrap with PermissionGuard if permission is required
              // Requirements: 7.2 - Display only menu items user has permission to access
              if (item.permission) {
                return (
                  <PermissionGuard key={item.id} permission={item.permission}>
                    {navButton}
                  </PermissionGuard>
                );
              }
              return <React.Fragment key={item.id}>{navButton}</React.Fragment>;
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-gray-800" role="contentinfo">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-yellow-400">
                  {currentUser?.name?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{currentUser?.name}</p>
                <p className="text-xs text-gray-400 truncate">{userRoles.map(formatRoleDisplay).join(', ') || 'Staff'}</p>
              </div>
            </div>
            <Button 
              onClick={handleLogout} 
              variant="outline" 
              size="sm" 
              className="w-full border-gray-600 text-gray-300 hover:text-white hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2"
              aria-label="Sign out of PeoplePulse"
            >
              <LogOut className="w-4 h-4 mr-2" aria-hidden="true" /> Sign Out
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default MobileNavigation;